import { useState } from "react";
import { Link } from "wouter";
import { useAuth } from "../../auth/AuthContext";
import { canManageFleet } from "../../auth/roleView";
import { ApiError } from "../../api/errors";
import { useDeleteInstance, useInstances, useProviderCredentials } from "../../api/queries";
import type { Instance } from "../../api/types";
import { EmptyState } from "../../components/AsyncStates";
import { Button } from "../../components/Button";
import { DataTable, type DataTableColumn } from "../../components/DataTable";
import { InstanceStatusPill } from "../../components/StatePill";
import { PageHeader } from "../../components/PageHeader";
import { PaginatorControls, usePaginator } from "../../components/Paginator";
import { QueryStates } from "../../components/QueryStates";
import { useToast } from "../../toast/ToastContext";
import { FleetSubNav } from "../fleet/FleetSubNav";
import { DeleteInstanceDialog } from "./DeleteInstanceDialog";
import { ProvisionInstanceModal } from "./ProvisionInstanceModal";
import "../../components/Form.css";

export function InstancesListPage() {
  const { session } = useAuth();
  const { pushToast } = useToast();
  const paginator = usePaginator();
  const { data, isPending, isError, error, refetch, isFetching } = useInstances(undefined, {
    cursor: paginator.cursor,
  });
  const { data: credentialsPage } = useProviderCredentials();
  const deleteMutation = useDeleteInstance();

  const [provisionOpen, setProvisionOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Instance | null>(null);

  // Mirrors the server's authz matrix exactly (server/internal/authz/
  // model.go): instance:create is granted to operator AND admin (an
  // operator can spend against an EXISTING credential); instance:PURGE
  // (destroying a host, not just "deleting" a row) is admin-only, because it
  // destroys the root disk and every volume on it. UX only (S-8); the server
  // enforces the real decision.
  const canProvision = canManageFleet(session?.role);
  const canDestroy = session?.role === "admin";
  const hasCredentials = (credentialsPage?.items.length ?? 0) > 0;

  function handleDeleteConfirm() {
    if (!deleteTarget) return;
    const { id } = deleteTarget;
    deleteMutation.mutate(
      { id },
      {
        onSuccess: () => {
          pushToast({ title: `Instance "${id}" destroyed.`, tone: "success" });
          setDeleteTarget(null);
        },
        onError: (err) => {
          pushToast({
            title: `Couldn't destroy "${id}"`,
            description: err instanceof Error ? err.message : "Try again in a moment.",
            tone: "error",
          });
        },
      },
    );
  }

  const provisionButton = (
    <Button variant="primary" onClick={() => setProvisionOpen(true)} disabled={!hasCredentials}>
      Provision instance
    </Button>
  );

  const columns: DataTableColumn<Instance>[] = [
    { key: "id", header: "ID", cell: (i) => i.id, className: "data-table__primary data-table__mono" },
    { key: "status", header: "Status", cell: (i) => <InstanceStatusPill status={i.status} /> },
    { key: "region", header: "Region", cell: (i) => i.region, className: "data-table__mono" },
    { key: "serverType", header: "Server type", cell: (i) => i.serverType, className: "data-table__mono" },
    {
      key: "publicIpv4",
      header: "Public IPv4",
      cell: (i) => i.publicIpv4 || "—",
      className: (i) => (i.publicIpv4 ? "data-table__mono" : "data-table__empty-cell"),
    },
    ...(canDestroy
      ? [
          {
            key: "actions",
            header: <span className="visually-hidden">Actions</span>,
            cell: (i: Instance) => (
              <Button variant="danger" disabled={i.status === "deleting"} onClick={() => setDeleteTarget(i)}>
                Destroy
              </Button>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <FleetSubNav activeTab="instances" />
      <PageHeader
        title="Instances"
        description="Compute Comebye provisions on your behalf via a provider credential — the Hetzner half of the hero flow."
        actions={
          canProvision ? (
            provisionButton
          ) : (
            <span className="page-header__hint">Ask an operator or admin to provision instances.</span>
          )
        }
      />

      <p className="form-note">
        This is the raw provisioning-only view — most of the time <Link href="/hosts">Machines</Link> is what you
        want: it shows this instance and its enrolled machine together as one card.
      </p>

      <p className="form-note">
        Once an instance finishes provisioning, it shows up as an enrolled <Link href="/hosts">machine</Link> in
        Fleet — an instance is how it got provisioned, not something you'll look at separately once it's up.
      </p>

      {canProvision && !hasCredentials && (
        <p className="form-note">Add a provider credential first — there's nothing to provision on yet.</p>
      )}

      <QueryStates
        isLoading={isPending}
        loadingLabel="Loading instances…"
        isError={isError}
        error={error}
        onRetry={refetch}
        errorDescription={
          error instanceof Error ? error.message : "Couldn't load instances. Check your connection and try again."
        }
        renderError={(err) =>
          err instanceof ApiError && err.code === "not_implemented" ? (
            <EmptyState
              title="Cloud provisioning isn't turned on yet"
              description="Ask whoever runs this Comebye deployment to enable it and add a provider credential. Already have a server? You don't need provisioning for that."
              action={
                <Link href="/hosts" className="button button--secondary">
                  Connect an existing host
                </Link>
              }
            />
          ) : undefined
        }
        isEmpty={Boolean(data && data.items.length === 0)}
        empty={
          <EmptyState
            title="No instances provisioned yet"
            description="Provision one to give your services somewhere to run."
            action={canProvision && hasCredentials ? provisionButton : undefined}
          />
        }
      >
        {data && (
          <>
            <DataTable columns={columns} rows={data.items} rowKey={(i) => i.id} ariaBusy={isFetching} />
            <PaginatorControls
              itemCount={data.items.length}
              nextCursor={data.nextCursor}
              paginator={paginator}
              noun="instances"
            />
          </>
        )}
      </QueryStates>

      {canProvision && (
        <ProvisionInstanceModal open={provisionOpen} onClose={() => setProvisionOpen(false)} />
      )}

      {canDestroy && (
        <DeleteInstanceDialog
          instance={deleteTarget}
          isDeleting={deleteMutation.isPending}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </>
  );
}
